import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { createPortal } from 'react-dom';
import { fireTopKeyLayer, pushDialogLayer, pushKeyLayer } from './key-layers';
import styled from 'styled-components';

import {
  FONT_STACKS,
  GATEWAY_DEFAULT_SENTINEL,
  LANGUAGES,
  UI_SCALES,
  type FontPreference,
  type TelemetryPreference,
  type ThemePreference,
  type UiScale,
} from '@paradigm/terminal-core';

import { useTerminalConfig } from '../../context/config-context';
import {
  defaultModelLabel,
  resolveModelInCatalog,
} from '../../models/model-labels';
import {
  usePreferencesActions,
  usePreferencesState,
} from '../../providers/PreferencesProvider';
import {
  useSettingsActions,
  useSettingsState,
  type SettingsSection,
} from '../../providers/SettingsProvider';
import { useAgentState } from '../../providers/contexts';
import { useTabsActions } from '../../providers/TabsProvider';
import { MENU_ROW, MENU_ROW_ICON } from '../../styles/menu';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';
import PersonalizationSection from './PersonalizationSection';
import {
  AccountIcon,
  AppearanceIcon,
  CloseIcon,
  ModelIcon,
  PersonalizationIcon,
  PrivacyIcon,
  type IconProps,
} from '../icons';
import {
  Mono,
  Notice,
  PaneTitle,
  Row,
  RowHelp,
  RowLabel,
  RowText,
  StaticValue,
  TYPE,
} from './primitives';
import { SettingsSelect } from './SettingsSelect';

/**
 * Settings — a modal dialog over the terminal, the shape every comparable app
 * uses. Settings is somewhere you visit briefly and dismiss, so it overlays the
 * chat rather than replacing it; you return to exactly what you were doing.
 *
 * Inside, a section shell rather than a flat list of controls, because this is
 * the foundation the rest of the preferences will land in: adding a group means
 * one entry in "SECTIONS" and one pane component, and the nav, routing, and
 * layout come for free. The two panes here are the proof that the shape holds
 * for genuinely different kinds of preference — one client-only and instant
 * (theme), one that has to reconcile against live gateway data (the default
 * model).
 *
 * Portaled to "document.body" for the same reason the sidebar menus are: a host
 * panel using "backdrop-filter" becomes the containing block for
 * "position: fixed", which would trap the overlay inside the panel and let
 * "overflow: hidden" clip it.
 */

interface Section {
  readonly id: SettingsSection;
  /**
   * The whole row. No sub-label: one honest word per group says what a
   * description would, and a line of grey text under every row costs more
   * scanning than it repays.
   */
  readonly label: string;
  /**
   * Nav glyph. Decorative, not informative: every row is already labelled in
   * text, so the icons are "aria-hidden" and exist to make the list scannable
   * by shape once you know it — never to carry meaning the label doesn't.
   */
  readonly Icon: ComponentType<IconProps>;
}

const SECTIONS: readonly Section[] = [
  {
    id: 'personalization',
    label: 'Personalization',
    Icon: PersonalizationIcon,
  },
  { id: 'appearance', label: 'Appearance', Icon: AppearanceIcon },
  { id: 'model', label: 'Model', Icon: ModelIcon },
  { id: 'privacy', label: 'Privacy', Icon: PrivacyIcon },
  { id: 'account', label: 'Account', Icon: AccountIcon },
];

/** Most-sharing first, so the control reads as a scale toward privacy. */
const TELEMETRY_OPTIONS: readonly {
  readonly value: TelemetryPreference;
  readonly label: string;
  readonly help: string;
}[] = [
  {
    value: 'identified',
    label: 'Identified',
    help: 'Adds your account to error reports, so support can look up a problem you tell us about.',
  },
  {
    value: 'anonymous',
    label: 'Anonymous',
    // States what the data does NOT contain, rather than what it does not
    // reveal. The code is a salted hash of the account id and the salt ships
    // in the bundle, so anyone holding both it and a list of account ids can
    // match them back (see `pseudonymFor`) — a sentence promising anonymity
    // would be false, and one hedging about pen names was accurate but
    // wordier than the claim needs. mj850's wording, which is the claim
    // itself: no account identifier in the reports.
    help: 'Error details under a random account pseudonym, so they do not contain your account identifier.',
  },
  {
    value: 'minimal',
    label: 'Minimal',
    help: 'Only whether chats succeed or fail, and why. No error text, no chat or message references, nothing linking two reports to you.',
  },
];

/**
 * Language options for the picker: "auto" first, then the offered languages.
 *
 * Sorted by their own names rather than by tag, since the endonym is what the
 * reader is scanning. "localeCompare" with no locale, because there is no one
 * correct collation across a list this mixed and any stable order beats
 * insertion order.
 */
const LANGUAGE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'auto', label: 'Auto (match my message)' },
  ...Object.entries(LANGUAGES)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

const THEME_OPTIONS: readonly {
  readonly value: ThemePreference;
  readonly label: string;
}[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const FONT_OPTIONS: readonly {
  readonly value: FontPreference;
  readonly label: string;
}[] = [
  { value: 'default', label: 'Default (Roboto)' },
  { value: 'system', label: 'System' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Monospace' },
];

/**
 * Where a tick sits along the track, as a percentage of its width.
 *
 * Evenly spaced by INDEX, not by value: the slider steps one detent at a time,
 * so the labels have to line up with those steps. Spacing them by value would
 * put 150% four times further from 125% than 90% is from 80%, while the thumb
 * still moved one stop — the label and the thumb would visibly disagree.
 */
function tickOffset(scale: UiScale): number {
  const index = UI_SCALES.indexOf(scale);
  return (index / (UI_SCALES.length - 1)) * 100;
}

/**
 * Sentinel for "no explicit default" — a menu value must be a string. Defined
 * in core, beside the validator that rejects it should it ever reach storage.
 */
const GATEWAY_DEFAULT = GATEWAY_DEFAULT_SENTINEL;

/**
 * What the browser will stop on for Tab, in document order.
 *
 * Deliberately not filtered by visibility. Every pane in this dialog is
 * conditionally UNMOUNTED rather than hidden, so there is nothing invisible to
 * exclude — and the usual "offsetParent != null" test would silently pass
 * everything in jsdom, which has no layout, leaving the trap untested while
 * looking correct.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** `SettingsSelect` stamps this on the panel it portals to document.body, which
 *  is how the dialog tells its own dropdown from one behind its scrim. */
const SETTINGS_TITLE_ID = 'dime-settings-title';

/** The hand-rolled sidebar menus. Dismissed on mount, but should one be open
 *  over the dialog, a backdrop press is its click-away, not ours. */
const SIDEBAR_MENU_SELECTOR = '[data-account-menu], [data-context-menu]';

export default function SettingsView() {
  const { settingsOpen, returnFocusTo, sectionRequest } = useSettingsState();
  const { closeSettings } = useSettingsActions();

  // Unmounted while closed, so each open starts fresh and no stale
  // gateway-dependent state lingers behind the backdrop.
  if (!settingsOpen) return null;
  return (
    <SettingsDialog
      onClose={closeSettings}
      returnFocusTo={returnFocusTo}
      sectionRequest={sectionRequest}
    />
  );
}

interface DragOffset {
  readonly x: number;
  readonly y: number;
}

/** The card as laid out with NO transform — the frame clamping works in. */
interface DragBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Is one of this dialog's own menus open? Its dropdowns portal to
 * document.body and carry the dialog's title id; anything else counts only if
 * it is inside the dialog, so a menu on the page behind the scrim never makes
 * the dialog stand down.
 */
function menuAboveDialog(dialog: HTMLElement | null): boolean {
  if (
    dialog !== null &&
    document.querySelector(
      `[data-settings-select-menu="${dialog.getAttribute('aria-labelledby') ?? ''}"]`,
    ) !== null
  ) {
    return true;
  }
  const menus = document.querySelectorAll('[data-settings-menu]');
  if (dialog === null) return menus.length > 0;
  return Array.from(menus).some((el) => dialog.contains(el));
}

/**
 * Drag the dialog by its header, clamped to the viewport. The offset is a
 * `translate` written straight to the node (state would re-render the whole
 * dialog per pointermove); closing forgets it, so every open starts centred.
 */
function useDialogDrag(dialogRef: React.RefObject<HTMLDivElement | null>): {
  readonly dragging: boolean;
  readonly onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
} {
  const offset = useRef<DragOffset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const apply = useCallback(() => {
    const el = dialogRef.current;
    if (el == null) return;
    const { x, y } = offset.current;
    el.style.transform = x === 0 && y === 0 ? '' : `translate(${x}px, ${y}px)`;
  }, [dialogRef]);
  const from = useRef<{
    pointerX: number;
    pointerY: number;
    origin: DragOffset;
    base: DragBox;
  } | null>(null);

  const clamp = useCallback((next: DragOffset, base: DragBox): DragOffset => {
    // A card bigger than the window has no slack in that axis.
    const maxX = Math.max(0, window.innerWidth - (base.left + base.width));
    const minX = Math.min(0, -base.left);
    const maxY = Math.max(0, window.innerHeight - (base.top + base.height));
    const minY = Math.min(0, -base.top);
    return {
      x: Math.min(maxX, Math.max(minX, next.x)),
      y: Math.min(maxY, Math.max(minY, next.y)),
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      // Full-bleed on a phone: nowhere to drag to.
      if (window.matchMedia?.('(max-width: 640px)').matches === true) return;
      if ((e.target as HTMLElement).closest('button') != null) return;
      const el = dialogRef.current;
      if (el == null) return;
      const rect = el.getBoundingClientRect();
      from.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        origin: offset.current,
        base: {
          left: rect.left - offset.current.x,
          top: rect.top - offset.current.y,
          width: rect.width,
          height: rect.height,
        },
      };
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [dialogRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const start = from.current;
      if (start == null) return;
      offset.current = clamp(
        {
          x: start.origin.x + (e.clientX - start.pointerX),
          y: start.origin.y + (e.clientY - start.pointerY),
        },
        start.base,
      );
      apply();
    },
    [apply, clamp],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (from.current == null) return;
    from.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  useEffect(() => {
    function onResize(): void {
      const prev = offset.current;
      if (prev.x === 0 && prev.y === 0) return;
      const el = dialogRef.current;
      if (el == null) return;
      const rect = el.getBoundingClientRect();
      offset.current = clamp(prev, {
        left: rect.left - prev.x,
        top: rect.top - prev.y,
        width: rect.width,
        height: rect.height,
      });
      apply();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [apply, clamp, dialogRef]);

  return { dragging, onPointerDown, onPointerMove, onPointerUp };
}

function SettingsDialog({
  onClose,
  returnFocusTo,
  sectionRequest,
}: {
  readonly onClose: () => void;
  readonly returnFocusTo: HTMLElement | null;
  readonly sectionRequest: { readonly section: SettingsSection } | null;
}) {
  const config = useTerminalConfig();
  const { setActiveTab } = useTabsActions();
  // No host-supplied identity (the legacy pairing path has none) means there
  // is no account to show.
  const accountEnabled = config.user != null;
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const drag = useDialogDrag(dialogRef);

  // Appearance is NOT filtered on `features.appearance`: the flag gates only
  // the rows that need the host's cooperation to take effect (theme, scale,
  // font — see `AppearanceSection`), and the section also carries Language,
  // which the terminal applies itself on the wire. Personalization owns its
  // own "this gateway can't do that" state.
  const visibleSections = useMemo(
    () => SECTIONS.filter((s) => s.id !== 'account' || accountEnabled),
    [accountEnabled],
  );

  // A requested section wins, but only if it is actually available — a
  // deep-link to a section this host has gated off must land somewhere real
  // rather than on an empty pane.
  const [section, setSection] = useState<SettingsSection>(() => {
    const requested = visibleSections.find(
      (s) => s.id === sectionRequest?.section,
    );
    return requested?.id ?? visibleSections[0]?.id ?? 'model';
  });

  // Follow a deep-link made while ALREADY open: `openSettings({section})`
  // with the dialog up publishes a new request object (never a reopen — see
  // `SettingsProvider`), and the dialog navigates to it. Adjusted during
  // render, the same pattern as `PreferencesProvider`'s account swap: state
  // derived from a prop change, with no committed frame in between. Compared
  // by identity, since the provider mints a fresh object per request — a
  // repeat of the same section after the user has navigated away must
  // navigate again.
  const [seenRequest, setSeenRequest] = useState(sectionRequest);
  if (sectionRequest !== seenRequest) {
    setSeenRequest(sectionRequest);
    const requested = visibleSections.find(
      (s) => s.id === sectionRequest?.section,
    );
    if (requested != null) setSection(requested.id);
  }

  // Dismiss any menu left open UNDERNEATH us, before anything below runs.
  //
  // The accelerator opens settings without moving focus or the pointer, so a
  // Radix menu already up in the chat never learns it has been covered: it
  // keeps its focus trap, which fights this dialog's for every Tab, and it
  // keeps the page's pointer-events suppressed (the Backdrop now overrides
  // that, but a menu still trapping focus behind a modal is wrong on its own).
  //
  // Escape is Radix's own documented dismissal, so this uses the public
  // contract rather than reaching into the library. Declared FIRST so it runs
  // before the key handler below is attached — otherwise this synthetic
  // Escape would bubble straight into our own listener and close the dialog we
  // are in the middle of opening.
  useEffect(() => {
    for (const layer of document.querySelectorAll(
      '[data-radix-menu-content]',
    )) {
      layer.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
      );
    }
    // The app's own layers (account, session/folder context, the composer's
    // context meter) are not Radix, but honor the same public contract — each
    // claims Escape on window capture. Without this they SURVIVE the modal,
    // and what that costs differs by layer: the menus float above the
    // backdrop and stay clickable while `aria-modal` tells AT the page behind
    // is inert; the context-meter panel paints UNDER the scrim and holds
    // nothing tabbable, so its cost is the keystroke — claiming Escape on
    // window capture, it runs ahead of this dialog's own handler and eats the
    // dialog's first Escape. Dispatched on window (their listener's target)
    // and only when one is actually open; the dialog's own Escape handler is
    // attached by a later effect, so this synthetic press cannot close the
    // dialog itself.
    if (
      document.querySelector(
        '[data-account-menu], [data-context-menu], [data-context-meter]',
      ) != null
    ) {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    }
  }, []);

  // Escape closes, and focus moves into the dialog on open then back to the
  // trigger on close — otherwise a keyboard user is left tabbing through the
  // chat behind the backdrop.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    // Re-assert once the dismissal above has settled.
    //
    // A menu closing underneath us tears down its focus scope, which BLURS
    // whatever holds focus — including the dialog we just focused a line ago.
    // Radix runs that from a `setTimeout(0)`, so the dialog wins the commit
    // and loses the tick, and focus lands on `<body>`: the caret is nowhere,
    // and the first Tab starts from the top of the page.
    //
    // Bails if the dialog has since unmounted, and if focus is already inside
    // it — so this cannot yank focus off a control the user has clicked, or
    // fight a dropdown's own roving focus.
    const settle = setTimeout(() => {
      const el = dialogRef.current;
      if (el == null || !document.contains(el)) return;
      if (el.contains(document.activeElement)) return;
      el.focus();
    }, 0);

    /**
     * Keep Tab inside the dialog.
     *
     * "aria-modal" tells assistive technology the rest of the page is inert,
     * but it does not move the browser's tab order — and because the dialog is
     * portaled to the END of document.body, Shift+Tab off its first control
     * (or Tab off its last) walks straight into the chat behind the backdrop,
     * where every stop is invisible under the scrim. So the wrap is enforced
     * here.
     *
     * Bound on the document rather than the dialog element because a Radix
     * menu's panel is portaled outside the dialog's DOM subtree; a listener on
     * the dialog would miss keys pressed while a menu has focus.
     */
    function onTab(e: globalThis.KeyboardEvent): void {
      const dialogEl = dialogRef.current;
      if (dialogEl == null) return;
      // An open menu of ours runs its own roving focus and closes on Tab.
      if (menuAboveDialog(dialogEl)) return;

      const items = Array.from(
        dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      const active = document.activeElement as HTMLElement | null;
      const inside = active != null && dialogEl.contains(active);

      // Nothing to land on (every control disabled while preferences load):
      // hold focus on the card itself rather than letting Tab leak out.
      if (items.length === 0) {
        e.preventDefault();
        dialogEl.focus();
        return;
      }

      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey) {
        // The dialog element itself is the first stop (tabIndex -1, focused on
        // open), so backwards from it belongs at the end too.
        if (!inside || active === first || active === dialogEl) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Tab only; Escape belongs to the shared key stack below.
    function onKeyDown(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Tab') onTab(e);
    }
    document.addEventListener('keydown', onKeyDown, true);
    const releaseDialog = pushDialogLayer(() => dialogRef.current);
    // Escape peels one layer. Inline edits inside the dialog sit above this
    // layer on the stack; Radix dropdowns do not, so they are checked here.
    const releaseLayer = pushKeyLayer('Escape', {
      owner: () => dialogRef.current,
      fire: () => {
        if (menuAboveDialog(dialogRef.current)) return false;
        onCloseRef.current();
        return true;
      },
    });
    return () => {
      clearTimeout(settle);
      releaseLayer();
      releaseDialog();
      document.removeEventListener('keydown', onKeyDown, true);
      // The opener's target, then our own capture, skipping detached nodes
      // (the menu item that opened this dialog unmounts with its menu).
      const target = [returnFocusTo, previouslyFocused].find(
        (el) => el != null && document.contains(el),
      );
      target?.focus?.();
    };
  }, [onClose, returnFocusTo]);

  // Freeze the page behind the scrim. Without this a wheel over the dialog
  // scrolls whatever is underneath it, so dismissing returns you somewhere you
  // never navigated to. The previous value is restored rather than cleared, so
  // a host that sets its own body overflow gets it back.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const onBackdropPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Primary button, starting on the dim area — not a press bubbling out
      // of the dialog, nor a drag that began inside it.
      if (e.button !== 0 || e.target !== e.currentTarget) return;
      // An open menu takes this press as its own click-away.
      if (
        menuAboveDialog(dialogRef.current) ||
        document.querySelector(SIDEBAR_MENU_SELECTOR) !== null
      ) {
        return;
      }
      // The same one-layer rule as Escape: a live inline edit is peeled
      // first, and the dialog closes on the next press.
      fireTopKeyLayer('Escape');
    },
    [],
  );

  return createPortal(
    <Backdrop onPointerDown={onBackdropPointerDown} data-settings-backdrop>
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={SETTINGS_TITLE_ID}
        tabIndex={-1}
      >
        <DialogHeader
          $dragging={drag.dragging}
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
          onPointerCancel={drag.onPointerUp}
        >
          <DialogTitle id={SETTINGS_TITLE_ID}>Settings</DialogTitle>
          <CloseButton
            type="button"
            aria-label="Close settings"
            onClick={onClose}
          >
            <CloseIcon />
          </CloseButton>
        </DialogHeader>
        <DialogBody>
          <Nav aria-label="Settings sections">
            {visibleSections.map((s) => (
              <NavItem
                key={s.id}
                type="button"
                $active={section === s.id}
                aria-current={section === s.id}
                onClick={() => setSection(s.id)}
              >
                <NavIcon>
                  <s.Icon />
                </NavIcon>
                <NavLabel>{s.label}</NavLabel>
              </NavItem>
            ))}
          </Nav>
          <Pane>
            <PaneInner>
              {section === 'personalization' && (
                <PersonalizationSection
                  onOpenFullView={() => {
                    setActiveTab('personalization');
                    onClose();
                  }}
                />
              )}
              {section === 'appearance' && <AppearanceSection />}
              {section === 'model' && <ModelSection />}
              {section === 'privacy' && <PrivacySection />}
              {section === 'account' && accountEnabled && <AccountSection />}
            </PaneInner>
          </Pane>
        </DialogBody>
      </Dialog>
    </Backdrop>,
    document.body,
  );
}

/**
 * Account — the host-owned identity facts. The display name used to live here
 * (as "Profile"); it moved to Personalization, where it is a view of USER.md.
 */
function AccountSection() {
  const { user } = useTerminalConfig();

  return (
    <>
      <PaneTitle>Account</PaneTitle>
      <Row>
        <RowText>
          <RowLabel>Email</RowLabel>
          <RowHelp>
            The address you signed up with. Changing it is handled by your
            sign-in provider.
          </RowHelp>
        </RowText>
        {/* Rendered as a value, not as a disabled input. A greyed-out text box
            invites a click and reads as broken; a plain value reads as a fact,
            which is what this is — an email is a sign-in credential, and
            changing one is a re-verification flow, not a settings edit. */}
        <StaticValue title={user?.email ?? undefined}>
          {user?.email != null && user.email.length > 0 ? user.email : '—'}
        </StaticValue>
      </Row>
    </>
  );
}

function AppearanceSection() {
  const { preferences, loaded } = usePreferencesState();
  const { setPreference } = usePreferencesActions();
  // Theme, scale and font are only PROMISES the host keeps — the terminal
  // records the choice and the host applies it from `onPreferencesChange` —
  // so those rows hide when the host hasn't opted in. Language is different:
  // it rides the outgoing message itself (`buildSendPayload`), needing
  // nothing from the host, so it never hides with them.
  const appearanceEnabled = useTerminalConfig().features?.appearance === true;

  return (
    <>
      <PaneTitle>Appearance</PaneTitle>
      <Row>
        <RowText>
          <RowLabel id="settings-language-label">Language</RowLabel>
          <RowHelp>
            The language replies come back in. The app&apos;s own buttons and
            menus stay in English.
          </RowHelp>
        </RowText>
        <SettingsSelect
          ownerId={SETTINGS_TITLE_ID}
          label="Language"
          disabled={!loaded}
          value={preferences.language}
          options={LANGUAGE_OPTIONS}
          onChange={(next) => setPreference('language', next)}
        />
      </Row>
      {appearanceEnabled && (
        <>
          <Row>
            <RowText>
              <RowLabel id="settings-theme-label">Theme</RowLabel>
              <RowHelp>
                System follows your operating system&apos;s light or dark
                setting.
              </RowHelp>
            </RowText>
            <Segmented role="group" aria-labelledby="settings-theme-label">
              {THEME_OPTIONS.map((option) => (
                <SegmentButton
                  key={option.value}
                  type="button"
                  disabled={!loaded}
                  $active={preferences.theme === option.value}
                  aria-pressed={preferences.theme === option.value}
                  onClick={() => setPreference('theme', option.value)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>
          </Row>
          <Row>
            <RowText>
              <RowLabel id="settings-scale-label">UI Scale</RowLabel>
              <RowHelp>
                Control the zoom level to adjust the size of the interface.
              </RowHelp>
            </RowText>
            <ScaleField>
              {/*
            A real range input rather than a div-and-pointer-handlers
            reimplementation: it comes with keyboard support (arrows, Home,
            End), the correct ARIA role and value announcements, and it
            respects the OS's own pointer conventions. The visual detents are
            painted underneath; the input itself stays the interactive layer.

            Indexed rather than valued — "step" only walks an even ladder, and
            ours is deliberately uneven (the gaps widen as it grows, because a
            10% change matters less the further up you are).
          */}
              <ScaleSlider
                type="range"
                min={0}
                max={UI_SCALES.length - 1}
                step={1}
                disabled={!loaded}
                value={Math.max(0, UI_SCALES.indexOf(preferences.uiScale))}
                aria-labelledby="settings-scale-label"
                aria-valuetext={`${preferences.uiScale}%`}
                onChange={(event) => {
                  const next = UI_SCALES[Number(event.target.value)];
                  if (next != null) setPreference('uiScale', next);
                }}
              />
              {/*
            The labels are click targets as well as marks — reaching for "125%"
            and finding it inert is the obvious thing to try and a small
            annoyance to get wrong.

            Buttons for the pointer affordance, but "aria-hidden" and out of the
            tab order: the slider above is already the labelled control for this
            value and handles arrows, Home and End. Exposing six more focus
            stops for the same setting would pad every keyboard user's path
            through the pane to say nothing new.
          */}
              <ScaleTicks aria-hidden="true">
                {UI_SCALES.map((scale) => (
                  <ScaleTick
                    key={scale}
                    type="button"
                    tabIndex={-1}
                    disabled={!loaded}
                    $active={scale === preferences.uiScale}
                    style={{ left: `${tickOffset(scale)}%` }}
                    onClick={() => setPreference('uiScale', scale)}
                  >
                    {scale}%
                  </ScaleTick>
                ))}
              </ScaleTicks>
            </ScaleField>
          </Row>
          <Row>
            <RowText>
              <RowLabel id="settings-font-label">Terminal Font</RowLabel>
              <RowHelp>
                Applies to the whole app. Each option falls back to the closest
                font your system has.
              </RowHelp>
            </RowText>
            <SettingsSelect
              ownerId={SETTINGS_TITLE_ID}
              label="Terminal Font"
              disabled={!loaded}
              value={preferences.fontFamily}
              // Each row previewed in its own face, and the trigger in the chosen
              // one — so the list shows what you are picking rather than naming it.
              triggerStyle={{ fontFamily: FONT_STACKS[preferences.fontFamily] }}
              options={FONT_OPTIONS.map((option) => ({
                ...option,
                style: { fontFamily: FONT_STACKS[option.value] },
              }))}
              onChange={(next) => setPreference('fontFamily', next)}
            />
          </Row>
        </>
      )}
    </>
  );
}

/**
 * What the terminal may report when a chat fails. Not feature-gated: every
 * host owes users this.
 */
function PrivacySection() {
  const { preferences, loaded } = usePreferencesState();
  const { setPreference } = usePreferencesActions();
  const active = preferences.telemetry;
  // Both seams are optional and the embeds wire neither, so the pane has to
  // say what is true of THIS build rather than hide itself.
  //
  // Split per seam, because they carry different claims. The always-on count
  // is `onRunOutcome`'s alone — `emitChatError` returns early on Minimal — so
  // a host wiring only `onChatError` sent nothing on Minimal while the pane
  // promised a count. The tiers still govern what that host reports, which is
  // not the same as nothing.
  const config = useTerminalConfig();
  const counts = config.onRunOutcome != null;
  const reporting = counts || config.onChatError != null;

  return (
    <>
      <PaneTitle>Privacy</PaneTitle>
      <Row>
        <RowText>
          <RowLabel id="settings-telemetry-label">Error reporting</RowLabel>
          <RowHelp>
            What we may keep when a chat fails. Your messages are never
            included, on any setting, and credentials are stripped from error
            text before it leaves your device.
          </RowHelp>
        </RowText>
        <Segmented
          role="group"
          aria-labelledby="settings-telemetry-label"
          // Without this a screen reader announces only "Minimal, pressed" —
          // the sentence that says what the tier actually keeps is the whole
          // point of the control, and it was unreachable.
          aria-describedby="settings-telemetry-help"
        >
          {TELEMETRY_OPTIONS.map((option) => (
            <SegmentButton
              key={option.value}
              type="button"
              disabled={!loaded}
              $active={active === option.value}
              aria-pressed={active === option.value}
              onClick={() => setPreference('telemetry', option.value)}
            >
              {option.label}
            </SegmentButton>
          ))}
        </Segmented>
      </Row>
      <Row>
        <RowText>
          <RowHelp id="settings-telemetry-help">
            {TELEMETRY_OPTIONS.find((o) => o.value === active)?.help}
          </RowHelp>
          {counts ? (
            <RowHelp>
              An anonymous count of whether chats succeed or fail is always
              sent, including on Minimal. It carries no account, no chat and no
              text — only a result. We keep it unconditional because if the
              people having the worst experience are the ones most likely to
              switch reporting off, the numbers would improve as the product got
              worse, and we would not find out.
            </RowHelp>
          ) : reporting ? (
            <RowHelp>
              This build reports only what the setting above allows, and sends
              nothing at all on Minimal.
            </RowHelp>
          ) : (
            <RowHelp>
              This app does not send error reports anywhere. Your choice is
              still saved, for a build that does.
            </RowHelp>
          )}
          {/* Said plainly rather than left to be discovered: a privacy
              setting that quietly does not follow you is worse than one that
              says so. See `DEVICE_LOCAL_PREFERENCE_KEYS`. */}
          <RowHelp>
            This choice is saved on this device only — your other devices keep
            their own.
          </RowHelp>
        </RowText>
      </Row>
    </>
  );
}

function ModelSection() {
  const { preferences, loaded } = usePreferencesState();
  const { setPreference } = usePreferencesActions();
  const { models, modelsStatus } = useAgentState();

  const savedId = preferences.defaultModelId;

  // A stored id can outlive its catalog entry (a gateway drops a model, or the
  // user switches to a gateway that never served it). Resolve against the live
  // catalog so the select can say so instead of silently showing the first
  // option as though that were the saved choice.
  const resolved = useMemo(
    () => (savedId == null ? null : resolveModelInCatalog(models, savedId)),
    [models, savedId],
  );
  const catalogReady = modelsStatus === 'ready';
  const orphaned = savedId != null && resolved == null && catalogReady;

  // While the catalog is unknown — still loading, or the read failed — a saved
  // default is STILL what new chats get: the seed path pushes it unverified
  // rather than discarding a preference it merely cannot confirm, and lets the
  // read-back correct it. So name that id here.
  //
  // Showing the status text in the value's place instead ("Loading models…",
  // "Models unavailable") said the opposite of what the app was about to do —
  // the user read "no default is in effect" while their default was being sent.
  // The status belongs to the option list, which really is empty; it does not
  // belong to a value that exists.
  const unconfirmedDefault = !catalogReady && savedId != null;

  const sorted = useMemo(
    () =>
      [...models].sort((a, b) =>
        defaultModelLabel(a.id).localeCompare(defaultModelLabel(b.id)),
      ),
    [models],
  );

  return (
    <>
      <PaneTitle>Model</PaneTitle>
      <Row>
        <RowText>
          <RowLabel id="settings-default-model-label">
            Default for new chats
          </RowLabel>
          <RowHelp>
            Applies to new sessions. Use the model picker in the composer to
            switch the model of an existing chat.
          </RowHelp>
        </RowText>
        <SettingsSelect
          ownerId={SETTINGS_TITLE_ID}
          label="Default for new chats"
          // The catalog comes only from the gateway, so there is nothing
          // truthful to offer while it is in flight.
          disabled={!loaded || !catalogReady}
          value={
            unconfirmedDefault ? savedId! : (resolved?.id ?? GATEWAY_DEFAULT)
          }
          // Matches no option while the catalog is unknown, so the trigger
          // falls back to this — the saved id, named rather than replaced by a
          // status line.
          placeholder={
            unconfirmedDefault ? defaultModelLabel(savedId!) : undefined
          }
          options={[
            {
              value: GATEWAY_DEFAULT,
              label: catalogReady
                ? 'Gateway default'
                : modelsStatus === 'unavailable'
                  ? 'Models unavailable'
                  : 'Loading models…',
            },
            ...sorted.map((model) => ({
              value: model.id,
              label:
                model.label.length > 0
                  ? model.label
                  : defaultModelLabel(model.id),
            })),
          ]}
          onChange={(next) =>
            setPreference(
              'defaultModelId',
              next === GATEWAY_DEFAULT ? null : next,
            )
          }
        />
      </Row>
      {orphaned && (
        <Notice>
          Your saved default (<Mono>{savedId}</Mono>) isn&apos;t in this
          gateway&apos;s catalog, so new chats fall back to the gateway default.
          Pick a model above to replace it.
        </Notice>
      )}
    </>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /*
    Assert our own interactivity rather than inheriting the page's.

    Radix's dismissable layers set "pointer-events: none" on <body> while a
    menu is open, to make everything outside the menu unclickable. This dialog
    portals INTO that body, so it inherited the block: with a picker still open
    behind the scrim, every control here — close, nav, the selects, backdrop
    dismiss — was inert, and only Escape got the user out. Measured in
    Chromium: no click of any kind lands, and this one line restores all of
    them.

    Stated unconditionally because it is a property of being the top layer,
    not a workaround for one library: whatever sits underneath, a modal the
    user is looking at has to be clickable.
  */
  pointer-events: auto;
  /* Dimmed with black in BOTH themes: a white scrim over the light theme
     wouldn't read as "behind a modal" at all. */
  background: rgba(0, 0, 0, 0.45);

  @media (max-width: 640px) {
    /* Full-bleed on a phone — a centred card with margins wastes the only
       screen space there is. */
    padding: 0;
  }
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /*
    Portrait-ish, not a letterbox. At 860x500 this was 1.73:1 — wide enough
    that a three-row nav sat in a mostly empty column and each pane's controls
    stretched across more width than a label-plus-control needs. Comparable
    settings dialogs land near 1.3:1; 720x620 is 1.16:1, which keeps rows a
    comfortable reading width and leaves vertical room for the panes that grow.

    The fixed height is capped by max-height below, so on a short window it
    shrinks rather than overflowing the viewport.

    (No backticks in this comment: styled-components parses it as a template
    literal and one would terminate the string, blanking the app — tsc and
    ESLint both accept it, so only the browser catches it.)
  */
  max-width: 720px;
  height: 620px;
  max-height: 100%;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  /* --bg-color-container is the "raised surface" token, which is exactly what a
     modal is — and it elevates in the right DIRECTION in both themes: darker
     than white on light, lighter than near-black on dark. --bg-color-page made
     the dark dialog the same near-black as the page behind it, so the card had
     no edge at all. (No backticks in here: this is a styled-components template
     literal, and one would terminate the string.) */
  background: var(--bg-color-container);
  color: var(--text-primary);
  overflow: hidden;
  /* Carries the elevation on light, where the page is white and the fill alone
     is nearly invisible; on dark the fill and border do the work, since a soft
     shadow over near-black shows nothing. */
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);

  /*
    The rules below are VIEWPORT media queries, and deliberately so.

    A card-relative version was tried and reverted: it read as the obvious
    improvement — "these breakpoints are about how much room the card has" —
    and it is simply false here. This card is 720px by design and is still
    ~720px at a 768px viewport (the backdrop's 24px padding either side), so
    its own width cannot tell a phone from a desktop. Worse, 720 is under the
    768 threshold, so translating the numbers unchanged made the narrow layout
    the PERMANENT one: every desktop got the stacked nav.

    The known cost is UI scale. "zoom" scales every used length without
    re-evaluating media queries, so at 150% on a 1000px window the effective
    viewport is ~667px while these still see 1000, and the stacked layouts
    arrive later than they should. Real, but strictly smaller than getting the
    default desktop layout wrong, and it wants the effective viewport
    (innerWidth / zoom factor) rather than the card — a separate change.
  */
  &:focus {
    /* Focused programmatically on open purely to move the caret into the
       dialog — showing a ring around the whole card would be noise. */
    outline: none;
  }

  @media (max-width: 640px) {
    height: 100%;
    max-width: none;
    border: none;
    border-radius: 0;
  }
`;

const DialogHeader = styled.div<{ $dragging: boolean }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--divider-color);
  /* The drag handle. The grab cursor is the only affordance a movable window
     gets, and it belongs on the bar rather than the whole card: a card-wide
     drag would fight every selection and every control inside it. */
  cursor: ${(p) => (p.$dragging ? 'grabbing' : 'grab')};
  /* A drag that crosses the title would otherwise select it, leaving the text
     highlighted after the card lands. */
  user-select: none;
  /* Chromium starts a native text/image drag from a mousedown on a bar like
     this, which cancels the pointer capture mid-move. */
  touch-action: none;

  @media (max-width: 640px) {
    /* Full-bleed: nowhere to move to, so no handle is offered. */
    cursor: default;
  }
`;

const DialogTitle = styled.h2`
  margin: 0;
  ${TYPE.heading}
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
    color: var(--text-primary);
  }
`;

const DialogBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;

  /* Stack below md: a two-pane settings screen at phone width leaves neither
     pane usable. The nav becomes a horizontal strip above the pane. */
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Nav = styled.nav`
  flex: 0 0 auto;
  /* Narrowed with the dialog so the split stays near a fifth — at 190px in a
     720px card the nav read as a column of mostly empty space. */
  width: 168px;
  padding: 12px 8px;
  border-right: 1px solid var(--divider-color);
  display: flex;
  flex-direction: column;
  /* Roomier than a menu's 2px. A menu is a transient list you flick down; this
     column is persistent furniture you read, and at 2px with four entries the
     rows ran together into one block of text. */
  gap: 6px;
  overflow-y: auto;
  ${THIN_SCROLLBAR}

  @media (max-width: 768px) {
    width: auto;
    flex-direction: row;
    border-right: none;
    border-bottom: 1px solid var(--divider-color);
    padding: 8px;
    overflow-x: auto;
  }
`;

const NavItem = styled.button<{ $active: boolean }>`
  ${MENU_ROW}
  /* Two things a menu row doesn't need and a nav row does.

     One: a persistent selected state. "--button-color", not
     "--button-color-dark" — this row sits on a "--bg-color-container"
     surface, and the resting shade is near-identical to that surface in the
     light theme (1.09:1 — effectively invisible). The higher-emphasis shade
     is the one that actually lifts off a container in both palettes.

     Two: room. The shared schema is tuned for a menu — a transient list that
     wants to be compact under the cursor — and this column is permanent, sits
     alone in its own pane, and is read rather than flicked through. So the
     row gets a taller floor and more side padding; the radius, type, hover
     and icon treatment still come from the schema, so the column and the
     menus that open over it stay recognisably the same control. */
  min-height: 34px;
  padding: 7px 10px;
  background: ${(p) => (p.$active ? 'var(--button-color)' : 'transparent')};

  @media (max-width: 768px) {
    width: auto;
    flex-shrink: 0;
  }
`;

/**
 * Dimmed until its row is active or hovered, so the column reads as a list of
 * words with marks beside them rather than a column of icons competing with
 * the labels for attention.
 */
const NavIcon = styled.span`
  ${MENU_ROW_ICON}

  ${NavItem}[aria-current='true'] & {
    color: var(--accent-color);
  }

  ${NavItem}:hover & {
    color: var(--text-primary);
  }
`;

const NavLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  ${THIN_SCROLLBAR}
`;

const PaneInner = styled.div`
  padding: 20px;
`;

const Segmented = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: 2px;
  gap: 2px;
  border-radius: 8px;
  /* The lifted shade, same reasoning as NavItem: the resting one vanishes
     against the container this track sits on in the light theme. */
  background: var(--button-color);
`;

/**
 * The scale slider: track, thumb, and a row of labelled detents beneath it.
 *
 * Sized in "ch" so the end labels have room without the track running to the
 * pane edge — the first and last ticks are centred on the track's ends, so
 * half a label overhangs each side.
 */
const ScaleField = styled.div`
  position: relative;
  flex-shrink: 0;
  /* Wider than the 220px dropdown on purpose: six labelled stops need the
     room, and squeezing them to match the column left the end labels almost
     touching. */
  width: 280px;
  padding-bottom: 20px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ScaleSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 18px;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  /* Track and thumb need per-engine selectors — a combined rule is dropped
     wholesale by both engines, so these are deliberately duplicated. */
  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 2px;
    background: var(--divider-color);
  }

  &::-moz-range-track {
    height: 4px;
    border-radius: 2px;
    background: var(--divider-color);
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    /* Centres the thumb on the 4px track: (4 - 14) / 2. */
    margin-top: -5px;
    border: none;
    border-radius: 50%;
    background: var(--accent-color);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: none;
    border-radius: 50%;
    background: var(--accent-color);
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 4px;
    border-radius: 2px;
  }
`;

const ScaleTicks = styled.div`
  position: absolute;
  inset: auto 0 0;
  height: 16px;
`;

const ScaleTick = styled.button<{ $active: boolean }>`
  all: unset;
  position: absolute;
  /* The inline "left" is the tick's centre, set per stop. */
  transform: translateX(-50%);
  /* Widens the target beyond the glyphs without moving them: the padding is
     symmetric, so the text stays centred on its stop. */
  padding: 3px 6px;
  margin: -3px -6px;
  cursor: pointer;
  ${TYPE.meta}
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  /* Colour alone marks the current stop. At 11px a weight bump reads as a
     rendering artefact — the label looks smudged, not emphasised — and it
     shifts the text's width, nudging its neighbours as you drag. */
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-label)')};

  &:hover:not(:disabled) {
    color: var(--text-primary);
  }

  &:disabled {
    cursor: default;
  }
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  padding: 5px 12px;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$active ? 'var(--bg-color-page)' : 'transparent')};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  font-family: inherit;
  ${TYPE.body}
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:not(:disabled):hover {
    color: var(--text-primary);
  }
`;
